/**
 * src/components/auth/SsoCallback.jsx
 *
 * Handles the OIDC redirect callback from Microsoft Entra ID.
 * Extracts the authorization code and state from the URL,
 * exchanges them for a SeaSuite JWT, and establishes the session.
 */

import { useEffect, useState } from 'react';
import { completeSso } from '../../api/ssoApi.js';
import { setSessionToken, setTenantId as setClientTenantId } from '../../api/azureClient.js';
import { logActivity } from '../../api/activityLogger.js';

export default function SsoCallback({ tenantId, onSessionEstablished }) {
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [error, setError] = useState('');

  useEffect(() => {
    async function handleCallback() {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const errorCode = params.get('error');
        const errorDesc = params.get('error_description');

        // Microsoft returned an error (user cancelled, access denied, etc.)
        if (errorCode) {
          setStatus('error');
          setError(errorDesc || `Microsoft login failed: ${errorCode}`);
          return;
        }

        if (!code || !state) {
          setStatus('error');
          setError('Missing authorization code. Please try signing in again.');
          return;
        }

        // Validate state matches what we stored
        const storedState = sessionStorage.getItem('sso_state');
        const storedTenantId = sessionStorage.getItem('sso_tenant_id');

        if (state !== storedState) {
          setStatus('error');
          setError('SSO state mismatch — possible CSRF attack. Please try again.');
          return;
        }

        // Clean up stored state
        sessionStorage.removeItem('sso_state');
        sessionStorage.removeItem('sso_tenant_id');

        const effectiveTenantId = storedTenantId || tenantId;
        const redirectUri = `${window.location.origin}/sso/callback`;

        // Exchange the code for a SeaSuite JWT
        const result = await completeSso({
          code,
          state,
          tenantId: effectiveTenantId,
          redirectUri,
        });

        if (result.error) {
          setStatus('error');
          setError(result.error);
          return;
        }

        // Establish session
        localStorage.setItem(`seasuite_token_${effectiveTenantId}`, result.token);
        localStorage.setItem(
          `seasuite_session_${effectiveTenantId}`,
          JSON.stringify({
            userId: result.user.id,
            username: result.user.username,
            displayName: result.user.displayName,
            role: result.user.role,
            themeId: result.user.themeId || null,
          })
        );

        setSessionToken(result.token);
        setClientTenantId(effectiveTenantId);

        // Log the SSO login
        logActivity(
          effectiveTenantId,
          {
            userId: result.user.id,
            username: result.user.username,
            displayName: result.user.displayName,
            role: result.user.role,
          },
          { module: 'Auth', actionType: 'login-sso' }
        );

        setStatus('success');

        // Notify parent or reload
        if (onSessionEstablished) {
          onSessionEstablished(result);
        } else {
          // Navigate to dashboard
          window.location.href = '/';
        }
      } catch (e) {
        console.error('[SsoCallback] Error:', e);
        setStatus('error');
        setError('An unexpected error occurred. Please try signing in again.');
      }
    }

    handleCallback();
  }, [tenantId, onSessionEstablished]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 400, padding: '0 24px', textAlign: 'center' }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            background: 'linear-gradient(135deg, var(--accent), #38bdf8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            fontWeight: 800,
            color: '#0d1f1e',
            fontFamily: 'var(--font-display)',
            margin: '0 auto 16px',
          }}
        >
          C
        </div>

        {status === 'processing' && (
          <div className="card" style={{ padding: 32 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg)', marginBottom: 12 }}>
              Completing sign in...
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg3)' }}>
              Exchanging credentials with Microsoft Entra ID
            </div>
            <div style={{ marginTop: 20 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  border: '3px solid var(--border)',
                  borderTopColor: 'var(--accent)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto',
                }}
              />
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="card" style={{ padding: 32 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ok)', marginBottom: 12 }}>
              Signed in successfully
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg3)' }}>
              Redirecting to your workspace...
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="card" style={{ padding: 32 }}>
            <div
              style={{ fontSize: 14, fontWeight: 600, color: 'var(--danger)', marginBottom: 12 }}
            >
              Sign in failed
            </div>
            <div
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                background: 'var(--danger-bg)',
                border: '1px solid rgba(251,113,133,0.25)',
                color: 'var(--danger)',
                fontSize: 12,
                marginBottom: 20,
              }}
            >
              {error}
            </div>
            <a
              href="/"
              className="btn btn-primary"
              style={{
                display: 'inline-flex',
                justifyContent: 'center',
                padding: '11px 24px',
                fontSize: 13,
                textDecoration: 'none',
              }}
            >
              Back to sign in
            </a>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
